import React, { useRef } from "react";
import {
  LazyMotion,
  domAnimation,
  m,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  Building2,
  Globe2,
  Layers,
  Sparkles,
  Target,
  Users,
  Zap,
} from "lucide-react";

import BasicNavbar from "./BasicNavbar";
import SiteFooter from "../../components/SiteFooter";
import ScrollingBanner from "./ScrollingBanner";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.06 * i,
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const investorLogoFiles = [
  "IDG.svg",
  "northernLight.svg",
  "miracleplus.svg",
  "shunwei.svg",
  "ics&s.svg",
  "lightCloud.svg",
  "xiaomi.svg",
  "cawincapital.svg",
  
];

const industries = [
  "本地生活",
  "大健康",
  "教育培训",
  "电商 & 零售",
  "科技行业",
];

const capabilities = [
  {
    iconSrc: "/images/about/transaction.png",
    title: "成交",
    subtitle: "以成交飞轮为核心的进攻型转化引擎",
    body: "拒绝被动应答，由 AI 掌控对话节奏，将每一段咨询转化为确定的经营业绩。",
    accent: "from-[#00b7ff]/30 via-fuchsia-500/20 to-transparent",
  },
  {
    iconSrc: "/images/about/privateDomain.png",
    title: "私域",
    subtitle: "深谙社交商业碎片的全局感知力",
    body: "打破平台壁垒与沟通断层，在非线性的私域环境中拼凑出最真实的客户动机。",
    accent: "from-violet-500/30 via-amber-500/15 to-transparent",
  },
  {
    iconSrc: "/images/about/AI.png",
    title: "AI",
    subtitle: "具备执行权限与思考力的数字销冠",
    body: "超越简单的“问答机器人”，是拥有 MCP 工具调度权与策略思考能力的 Agent。",
    accent: "from-emerald-500/25 via-cyan-500/20 to-transparent",
  },
] as const;

/** Same top rule as Mission band: subtle horizontal fade line */
function SectionTopDivider() {
  return (
    <div
      aria-hidden
      className="relative z-[1] h-px w-full shrink-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
    />
  );
}

function AmbientBackground() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      <div className="about-page-mesh absolute inset-0" />
      <div className="about-page-grid absolute inset-0 opacity-70" />
      <div
        className="about-page-orb about-page-orb-a -left-[20%] top-[8%] h-[min(55vw,420px)] w-[min(55vw,420px)] bg-gradient-to-br from-primary/80 via-secondary/50 to-emerald-500/40"
      />
      <div
        className="about-page-orb about-page-orb-b right-[-15%] top-[28%] h-[min(48vw,380px)] w-[min(48vw,380px)] bg-gradient-to-bl from-fuchsia-600/70 via-primary/40 to-transparent"
      />
      <div
        className="about-page-orb about-page-orb-c bottom-[-10%] left-[18%] h-[min(42vw,340px)] w-[min(42vw,340px)] bg-gradient-to-tr from-teal-500/50 via-primary/35 to-violet-600/40"
      />
    </div>
  );
}

export default function AboutUsPage() {
  const reduceMotion = useReducedMotion();
  const springConfig = { damping: 25, stiffness: 300 };

  const companyCardRef = useRef<HTMLDivElement | null>(null);
  const companyGlowRafRef = useRef<number | null>(null);
  const companyGlowPosRef = useRef({ x: 50, y: 30 });

  const companyTiltX = useMotionValue(0);
  const companyTiltY = useMotionValue(0);
  const companyRotateX = useTransform(companyTiltY, [-0.5, 0.5], [6, -6]);
  const companyRotateY = useTransform(companyTiltX, [-0.5, 0.5], [-6, 6]);
  const companyRotateXSpring = useSpring(companyRotateX, springConfig);
  const companyRotateYSpring = useSpring(companyRotateY, springConfig);

  const handleCompanyMouseMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = companyCardRef.current;
    if (!el) return;
    if (typeof window === "undefined") return;

    const rect = el.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

    companyGlowPosRef.current = {
      x: clamp(xPct, 0, 100),
      y: clamp(yPct, 0, 100),
    };

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const xNorm = (e.clientX - centerX) / (rect.width / 2);
    const yNorm = (e.clientY - centerY) / (rect.height / 2);

    companyTiltX.set(clamp(xNorm, -1, 1));
    companyTiltY.set(clamp(yNorm, -1, 1));

    if (companyGlowRafRef.current != null) return;
    companyGlowRafRef.current = window.requestAnimationFrame(() => {
      companyGlowRafRef.current = null;
      const { x, y } = companyGlowPosRef.current;
      el.style.setProperty("--mx", `${x}%`);
      el.style.setProperty("--my", `${y}%`);
    });
  };

  return (
    <div className="dark">
      <div
        className="bg-background relative flex min-h-screen min-h-dvh w-full flex-col overflow-x-hidden scroll-smooth"
        style={{ ["--navbar-height" as string]: "60px" } as React.CSSProperties}
      >
        <AmbientBackground />
        <BasicNavbar />

        <LazyMotion features={domAnimation} strict>
          <main className="relative z-10 flex w-full flex-1 flex-col items-center pt-[calc(var(--navbar-height)+var(--intl-notice-height,0px)+4rem)] pb-28 sm:pb-36">
            {/* Hero */}
            <section className="relative w-full max-w-5xl px-6 sm:px-8">
              <m.div
                className="mb-10 flex flex-col sm:mb-16"
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: {
                    transition: { staggerChildren: reduceMotion ? 0 : 0.08 },
                  },
                }}
              >
                <m.div
                  variants={fadeUp}
                  custom={0}
                  className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-default-400 backdrop-blur-md"
                >
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="tracking-wide">关于 3Chat.ai</span>
                </m.div>

                <m.h1
                  variants={fadeUp}
                  custom={1}
                  className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                >
                  我们为何而生？
                </m.h1>

                <m.div variants={fadeUp} custom={2} className="mt-2 max-w-xl">
                  <p className="max-w-5xl bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent leading-tight tracking-tight font-semibold text-lg">
                    每一句对话，都是一次成交的机会。
                  </p>
                </m.div>

                {/* <m.div variants={fadeUp} custom={3} className="h-px w-full max-w-md about-page-accent-line rounded-full" /> */}

                <m.div variants={fadeUp} custom={4} className="mt-8 max-w-5xl text-default-400">
                  <p className="text-base leading-relaxed sm:text-1rem">
                    我们坚信：如果一项功能不能帮助卖家更快、更稳地成交，那它就不该存在于 3Chat.ai
                    中。在高度碎片化的商业环境下，消费者的购买动机稍纵即逝，只有在被精准推进时才会做出购买决定。我们观察到，有限的人力正被低质量咨询耗光，而传统的被动回复工具无法替代卖家进行有策略的筛选与推进。
                  </p>
                  <p className="mt-8 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base font-medium text-foreground shadow-lg shadow-primary/5 backdrop-blur-md sm:text-2rem">
                    <span className="about-page-gradient-shimmer-text">
                      成交高于一切。其余一切紧随其后。
                    </span>
                  </p>
                </m.div>
              </m.div>
            </section>

            {/* Mission band */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <m.div
                className="relative mx-auto max-w-5xl px-6 py-24 sm:px-8"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={{
                  hidden: {},
                  show: {
                    transition: { staggerChildren: reduceMotion ? 0 : 0.1 },
                  },
                }}
              >
                <m.div variants={fadeUp} custom={0} className="mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-default-500">
                    Mission
                  </span>
                </m.div>
                <m.h1
                  variants={fadeUp}
                  custom={1}
                  className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                >
                  我们的使命
                </m.h1>
                <m.div variants={fadeUp} custom={2} className="mt-2 max-w-xl">
                  <p className="max-w-5xl bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent leading-tight tracking-tight font-semibold text-lg">
                    让每一次客户咨询，都能通往最终成交！
                  </p>
                </m.div>
                <m.div variants={fadeUp} custom={3} className="mt-8 max-w-5xl text-default-400">
                  <p className="text-base leading-relaxed sm:text-1rem">
                    3Chat.ai 是一款以成交为目标的私域 AI 售前智能体。它不只是回应咨询，而是理解真实意图、判断成交机会，并在对话中主动推进关键决策。从公域进线到私域跟进，{" "}
                    <span className="font-medium text-foreground">3Chat.ai</span>{" "}
                    自动完成筛选、引导与执行，让咨询不再停留在回复层，而是持续走向成交结果。3Chat.ai
                    适用于抖音、小红书、私域等真实卖家高频咨询场景，让每一次客户咨询，都能通往最终成交。
                  </p>
                  <p className="mt-8 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base font-medium text-foreground shadow-lg shadow-primary/5 backdrop-blur-md sm:text-2rem">
                    <span className="about-page-gradient-shimmer-text">
                      通过社交对话，实现客户营收的持续增长
                    </span>
                  </p>
                </m.div>
              </m.div>
            </section>

            {/* Company */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
              <m.div
                className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduceMotion ? 0 : 0.12 } },
                }}
              >
                <m.div variants={fadeUp} custom={0} className="flex flex-col">
                  <div className="mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-secondary" />
                    <span className="text-sm font-semibold uppercase tracking-[0.2em] text-default-500">
                      Company
                    </span>
                  </div>
                  <m.h1
                    variants={fadeUp}
                    custom={1}
                    className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                  >
                    3Chat & <br className="sm:hidden" />
                    纽酷科技
                  </m.h1>
                  <div className="mt-2 max-w-xl">
                    <p className="max-w-5xl bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent leading-tight tracking-tight font-semibold text-lg">
                      由具备十年经验的
                      <br />
                      <span className="bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent">
                        企业级 AI 公司打造。
                      </span>
                    </p>
                  </div>
                  <div className="mt-8 max-w-5xl space-y-8 text-default-400">
                    <p className="text-base leading-relaxed sm:text-1rem">
                      3Chat.ai 隶属于纽酷科技（NEW CORE TECH），一家成立于 2014 年、总部位于上海的前沿企业级 AI
                      公司。在过去的十年里，纽酷科技始终致力于在最复杂的工业与商业环境中，构建具备自主决策能力的 AI
                      系统。从赋能{" "}
                      <span className="font-semibold text-foreground">3,800+</span>{" "}
                      家全球制造业巨头的数字化大脑，到如今重塑社交商业的 3Chat.ai，我们的核心基因从未改变：
                    </p>
                    <p className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base font-medium text-foreground shadow-lg shadow-primary/5 backdrop-blur-md sm:text-2rem">
                      <span className="about-page-gradient-shimmer-text">
                        让 AI 走出“对话框”，进入“执行层”
                      </span>
                    </p>
                  </div>
                </m.div>

                <m.div
                  variants={scaleIn}
                  ref={companyCardRef}
                  whileHover={
                    { transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } }
                  }
                  onPointerMove={handleCompanyMouseMove}
                  onPointerLeave={() => {
                    const el = companyCardRef.current;
                    if (!el) return;
                    el.style.setProperty("--mx", "50%");
                    el.style.setProperty("--my", "30%");
                    companyTiltX.set(0);
                    companyTiltY.set(0);
                  }}
                  style={{
                    rotateX: companyRotateXSpring,
                    rotateY: companyRotateYSpring,
                    transformPerspective: 800,
                    transformStyle: "preserve-3d",
                    transformOrigin: "center",
                    // 强制圆角裁切：3D transform 下部分浏览器对 border-radius+overflow-hidden 会有瞬时裁切伪影
                    clipPath: "inset(0 round 24px)",
                    WebkitClipPath: "inset(0 round 24px)",
                  }}
                  className="group relative cursor-pointer overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-default-100/10 via-background to-background p-8 shadow-2xl shadow-primary/10 transition-shadow duration-300 hover:shadow-primary/20 will-change-transform"
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-0 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      borderRadius: "inherit",
                      // 在 3D transform 下，部分浏览器会出现 border-radius 裁切瞬间变直角的情况
                      // 用 clip-path 做强制圆角裁切，避免红圈直角蒙层。
                      clipPath: "inset(0 round 24px)",
                      WebkitClipPath: "inset(0 round 24px)",
                      background:
                        "radial-gradient(520px circle at var(--mx, 50%) var(--my, 30%), rgba(30, 174, 236, 0.26), rgba(58, 35, 235, 0.18) 35%, transparent 60%)",
                    }}
                  />
                  <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-gradient-to-br from-primary/40 to-fuchsia-500/30 blur-3xl" />
                  <div className="absolute -bottom-10 -left-10 h-36 w-36 rounded-full bg-gradient-to-tr from-emerald-500/30 to-primary/20 blur-3xl" />
                  <div className="relative z-10 flex flex-col gap-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
                        10
                      </span>
                      <span className="text-lg font-medium text-default-500 transition-colors duration-300 group-hover:text-default-800">年+</span>
                    </div>
                    <p className="text-sm text-default-400 transition-colors duration-300 group-hover:text-default-700">
                      企业级 AI 系统构建与落地经验
                    </p>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="rounded-2xl border border-white/5 bg-black/30 p-4 backdrop-blur-sm">
                        <Users className="mb-2 h-5 w-5 text-primary" />
                        <p className="text-xs text-default-500 transition-colors duration-300 group-hover:text-default-800">服务企业超过</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">3,800+</p>
                      </div>
                      <div className="rounded-2xl border border-white/5 bg-black/30 p-4 backdrop-blur-sm">
                        <Globe2 className="mb-2 h-5 w-5 text-emerald-400" />
                        <p className="text-xs text-default-500 transition-colors duration-300 group-hover:text-default-800">全球布局</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">多区域</p>
                      </div>
                    </div>
                  </div>
                </m.div>
              </m.div>
              </div>
            </section>

            {/* Investors */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
              <m.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduceMotion ? 0 : 0.04 } },
                }}
              >
                <m.div variants={fadeUp} custom={0} className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <div className="mb-4 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-400" />
                      <span className="text-sm font-semibold uppercase tracking-[0.2em] text-default-500">
                        Trust
                      </span>
                    </div>
                    <h2 className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground">
                      信任与同行
                    </h2>
                    <div className="mt-2 max-w-xl">
                      <p className="max-w-5xl bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent leading-tight tracking-tight font-semibold text-lg">
                        全球顶级投资者的信任选择。
                      </p>
                    </div>
                  </div>
                </m.div>

                <m.p
                  variants={fadeUp}
                  custom={1}
                  className="mt-8 max-w-5xl text-base leading-relaxed text-default-400 sm:text-1rem"
                >
                  我们的长期愿景得到了全球领先投资机构的支持，他们深信 AI 具有彻底改变商业成交方式的能力。
                </m.p>

                <m.div variants={fadeUp} custom={2} className="mt-8">
                  <ScrollingBanner shouldPauseOnHover gap="40px">
                    {investorLogoFiles.map((filename) => (
                      <div
                        key={filename}
                        className="flex-none text-foreground flex items-center justify-center"
                      >
                        <img
                          src={`/images/investor_logo/${encodeURIComponent(filename)}`}
                          alt={filename.replace(/\.svg$/i, "")}
                          className="h-10 w-auto max-w-[240px] object-contain opacity-100 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                        />
                      </div>
                    ))}
                  </ScrollingBanner>
                </m.div>
              </m.div>
              </div>
            </section>

            {/* Capabilities */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
              <m.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduceMotion ? 0 : 0.1 } },
                }}
              >
                <m.div
                  variants={fadeUp}
                  custom={0}
                  className="mb-4 flex items-center gap-2"
                >
                  <Layers className="h-5 w-5 text-fuchsia-400" />
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-default-500">
                    capability
                  </span>
                </m.div>
                <m.h2
                  variants={fadeUp}
                  custom={1}
                  className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                >
                  我们的能力，不只是功能集合
                </m.h2>
                <m.div variants={fadeUp} custom={2} className="mt-2 max-w-xl">
                  <p className="max-w-5xl bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent leading-tight tracking-tight font-semibold text-lg">
                    三大能力支柱，贯穿从进线到成交的全链路。
                  </p>
                </m.div>

                <div className="mt-8 grid gap-5 md:grid-cols-3">
                  {capabilities.map((cap, i) => {
                    return (
                      <m.div
                        key={cap.title}
                        variants={fadeUp}
                        custom={i + 3}
                        className="h-full"
                      >
                        {/* 位移只作用在内层，避免与 Framer 入场 transform 冲突；CSS transition 进/出对称无额外延迟 */}
                        <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-default-100/5 shadow-lg backdrop-blur-md transition-transform duration-150 ease-out will-change-transform hover:-translate-y-1.5">
                          <div
                            className={`pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity delay-0 duration-200 group-hover:opacity-100 ${cap.accent}`}
                          />
                          <div className="relative p-6">
                            <div className="flex items-start gap-4">
                              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-white/10 to-white/5 text-primary shadow-inner">
                                <img
                                  src={cap.iconSrc}
                                  alt={cap.title}
                                  loading="lazy"
                                  className="h-12 w-12 object-contain"
                                />
                              </div>
                              <div className="min-w-0 flex-1 pt-0.5">
                                <h3 className="text-xl font-semibold leading-tight text-foreground">
                                  {cap.title}
                                </h3>
                                <p className="mt-1 text-[14px] font-regular leading-snug text-default-700">
                                  {cap.subtitle}
                                </p>
                              </div>
                            </div>
                            <p className="relative mt-4 text-[14px] leading-relaxed text-default-400">
                              {cap.body}
                            </p>
                          </div>
                        </div>
                      </m.div>
                    );
                  })}
                </div>
              </m.div>
              </div>
            </section>

            {/* Industries + audience */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
              <m.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduceMotion ? 0 : 0.06 } },
                }}
              >
                <m.div
                  variants={fadeUp}
                  custom={0}
                  className="mb-4 flex items-center gap-2"
                >
                  <Globe2 className="h-5 w-5 text-emerald-400" />
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-default-500">
                    Industries
                  </span>
                </m.div>
                <m.h2
                  variants={fadeUp}
                  custom={1}
                  className="max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                >
                  我们为谁服务？
                </m.h2>

                <div className="mt-8 flex flex-wrap gap-2 sm:gap-3">
                  {industries.map((label, i) => (
                    <m.span
                      key={label}
                      variants={fadeUp}
                      custom={i + 2}
                      className="rounded-full border border-fuchsia-500/20 bg-gradient-to-r from-primary-500/20 via-transparent to-[#00b7ff]/10 px-4 py-2 text-sm font-medium italic text-default-600"
                    >
                      · {label}
                    </m.span>
                  ))}
                </div>

                <m.div
                  variants={fadeUp}
                  custom={industries.length + 2}
                  className="mt-8 max-w-5xl rounded-3xl text-default-400"
                >
                  <p className="text-base leading-relaxed sm:text-1rem">
                    3Chat.ai 为在小红书、抖音、微信、Instagram、Facebook、WhatsApp、Shopify
                    等社交或电商平台上开展业务的企业而设计。这些平台的消费者通过对话驱动信任，而信任驱动最终成交。从公域进线到私域跟进，3Chat.ai
                    帮助这些企业和团队，将海量的私信咨询转化为可规模化的成交机会！
                  </p>
                  <p className="mt-8 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base font-medium text-foreground shadow-lg shadow-primary/5 backdrop-blur-md sm:text-2rem">
                    <span className="about-page-gradient-shimmer-text">
                      助力每一个在社交平台挖掘增长的企业。
                    </span>
                  </p>
                </m.div>
              </m.div>
              </div>
            </section>

            {/* Vision */}
            <section className="relative mt-4 w-full">
              <SectionTopDivider />
              <div className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
              <m.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduceMotion ? 0 : 0.1 } },
                }}
                className="group relative cursor-pointer overflow-hidden rounded-[2rem] p-[1px] shadow-2xl shadow-primary/10 transition-shadow duration-300 before:absolute before:inset-0 before:z-0 before:rounded-[2rem] before:bg-gradient-to-r before:from-[#00b7ff]/85 before:via-primary/55 before:to-fuchsia-400/85 before:opacity-100 before:[background-size:240%_240%] before:animate-vision-border-shimmer after:absolute after:-inset-[10px] after:-z-10 after:rounded-[2.45rem] after:bg-gradient-to-r after:from-[#00b7ff]/40 after:via-primary/30 after:to-fuchsia-500/40 after:blur-4xl after:opacity-95 after:[background-size:240%_240%] after:animate-vision-border-shimmer hover:shadow-primary/25"
              >
                <div className="relative z-10 rounded-[calc(2rem-1px)] border border-primary/30 bg-background/95 p-8 backdrop-blur-xl sm:p-12 md:p-14">
                  <div className="absolute right-6 top-6 h-24 w-24 rounded-full bg-gradient-to-br from-primary/50 to-fuchsia-500/40 opacity-70 blur-2xl sm:right-10 sm:top-10" />
                  <m.h2
                    variants={fadeUp}
                    custom={0}
                    className="relative max-w-5xl text-[clamp(1.75rem,5vw,2.5rem)] font-semibold leading-[1.15] tracking-tight text-foreground"
                  >
                    我们的愿景
                  </m.h2>
                  <m.div variants={fadeUp} custom={1} className="mt-2 max-w-xl">
                    <p className="relative leading-tight tracking-tight font-semibold text-lg">
                      <span className="block bg-gradient-to-r from-default-200 to-default-800 bg-clip-text text-transparent transition-opacity duration-300 group-hover:opacity-0">
                        构建自主成交的商业未来。
                      </span>
                      <span className="pointer-events-none absolute inset-0 block text-default-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        构建自主成交的商业未来。
                      </span>
                    </p>
                  </m.div>
                  <m.p
                    variants={fadeUp}
                    custom={2}
                    className="relative mt-8 max-w-5xl text-base leading-relaxed text-default-400 transition-colors duration-300 group-hover:text-default-500 sm:text-1rem"
                  >
                    商业的未来是对话式的，更是自主化的。随着社交平台成为客户互动的核心点，企业需要能够理解意图、做出决策并大规模执行动作的
                    AI。我们正在构建这样的 AI 基础设施，旨在将每一次对话都转化为最终的成交。
                  </m.p>
                </div>
              </m.div>
              </div>
            </section>
          </main>
        </LazyMotion>

        <SiteFooter />
      </div>
    </div>
  );
}
